import { useState } from 'react';
import { HouseLine, MapPin, Signpost, Tag } from '@phosphor-icons/react';
import { FeldRaster, ImmoButton, LivePreviewCard, SegmentedControl, StepForm, TextFeld } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { naechsteHausBezeichnung } from '../../../../../lib/objekte/haeuser';
import { useObjektSeite } from '../ObjektSeitenKontext';

/**
 * „Haus / Liegenschaft hinzufügen“ (Prototyp): Bezeichnung und Adresse, Live-Vorschau.
 * Speichert wie das bestehende Häuser-Formular in objekt_haeuser. Zuordnen, Ändern und Löschen
 * bleiben im Häuser-Formular („Häuser verwalten“).
 */
export function NeuesHausFormular({ onAbbrechen, onAngelegt }: { onAbbrechen: () => void; onAngelegt: (bezeichnung: string) => void }) {
  const { daten, adresse } = useObjektSeite();
  const o = daten.objekt;
  const vorschlag = naechsteHausBezeichnung(daten.haeuser.map(h => ({ bezeichnung: h.bezeichnung ?? '' })));
  const [bezeichnung, setBezeichnung] = useState('');
  const [eigene, setEigene] = useState<'gleich' | 'eigen'>('gleich');
  const [strasse, setStrasse] = useState(o.strasse ?? '');
  const [hausnummer, setHausnummer] = useState('');
  const [plz, setPlz] = useState(o.plz ?? '');
  const [stadt, setStadt] = useState(o.stadt ?? '');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const name = bezeichnung.trim() || vorschlag;
  const doppelt = daten.haeuser.some(h => (h.bezeichnung ?? '').trim().toLowerCase() === name.toLowerCase());
  const adresseOk = eigene === 'gleich' || strasse.trim() !== '';
  const offen = [doppelt ? 'eindeutige Bezeichnung' : null, adresseOk ? null : 'Straße der eigenen Adresse'].filter((x): x is string => x !== null);

  const speichere = async () => {
    if (offen.length) return;
    setSpeichert(true);
    setFehler(null);
    const gleich = eigene === 'gleich';
    const { error } = await supabase.from('objekt_haeuser').insert({
      objekt_id: o.id,
      bezeichnung: name,
      strasse: (gleich ? o.strasse : strasse)?.trim() || null,
      hausnummer: (gleich ? o.hausnummer : hausnummer)?.trim() || null,
      plz: (gleich ? o.plz : plz)?.trim() || null,
      stadt: (gleich ? o.stadt : stadt)?.trim() || null,
    });
    setSpeichert(false);
    if (error) { setFehler(error.message); return; }
    onAngelegt(name);
  };

  const vorschauAdresse = eigene === 'gleich' ? adresse : [[strasse, hausnummer].filter(Boolean).join(' '), [plz, stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <span style={{ fontSize: 18, fontWeight: 800 }}>Neues Haus in der Liegenschaft</span>
      {daten.haeuser.length === 0 && (
        <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Mit dem ersten Haus wird das Objekt zur Liegenschaft. Bestehende Einheiten ordnest du danach unter „Häuser verwalten“ zu.</span>
      )}
      {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
      <StepForm
        hinweis={offen.length ? `Noch offen: ${offen.join(', ')}` : 'Alles bereit – Haus kann angelegt werden'}
        bereit={offen.length === 0}
        speichert={speichert}
        onSpeichern={() => { void speichere(); }}
        speichernLabel="Haus anlegen"
        sekundaer={<ImmoButton art="fuellung" groesse={48} pille={false} onClick={onAbbrechen}>Abbrechen</ImmoButton>}
        schritte={[
          { titel: 'Gebäude', untertitel: 'Bezeichnung des Hauses', erledigt: !doppelt,
            inhalt: <TextFeld label="Bezeichnung" icon={Tag} wert={bezeichnung} onChange={setBezeichnung} placeholder={vorschlag} /> },
          { titel: 'Adresse', untertitel: 'Gleiche Adresse wie das Objekt oder eigene Hausnummer/Straße', erledigt: adresseOk,
            inhalt: (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <SegmentedControl voll hoehe={36} ariaLabel="Adresse" wert={eigene} onChange={setEigene}
                  segmente={[{ key: 'gleich', label: 'Wie Objekt' }, { key: 'eigen', label: 'Eigene Adresse' }]} />
                {eigene === 'gleich' ? (
                  <span className="flex items-center" style={{ gap: 8, fontSize: 14, fontWeight: 600 }}><MapPin weight="fill" size={16} color="var(--acc)" />{adresse || 'Objekt ohne Adresse'}</span>
                ) : (
                  <FeldRaster minBreite={150}>
                    <TextFeld label="Straße" icon={Signpost} wert={strasse} onChange={setStrasse} voll />
                    <TextFeld label="Hausnr." wert={hausnummer} onChange={setHausnummer} />
                    <TextFeld label="PLZ" wert={plz} onChange={setPlz} inputMode="numeric" />
                    <TextFeld label="Ort" wert={stadt} onChange={setStadt} />
                  </FeldRaster>
                )}
              </div>
            ) },
        ]}
        vorschau={
          <LivePreviewCard>
            <div className="flex items-center" style={{ gap: 12 }}>
              <span className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 15, background: 'var(--acc-soft)' }}><HouseLine weight="fill" size={22} color="var(--acc)" /></span>
              <div className="flex flex-col min-w-0"><span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{name}</span><span style={{ fontSize: 12, color: 'var(--ink3)' }}>Haus in {daten.objekt.bezeichnung || 'der Liegenschaft'}</span></div>
            </div>
            <span className="flex items-center" style={{ gap: 8, fontSize: 12, color: 'var(--ink2)' }}><MapPin size={14} />{vorschauAdresse || 'Adresse fehlt'}</span>
          </LivePreviewCard>
        }
      />
    </div>
  );
}
